import React, { useState, useEffect } from 'react';
import { 
  X, Search, Sparkles, Microscope, Cpu, Database, BookOpen, 
  CheckCircle2, Terminal, ExternalLink, ShieldAlert, Activity, RefreshCw
} from 'lucide-react';

export interface ScientificSkillItem {
  name: string;
  dir: string;
  categoryUk: string;
  categoryEn: string;
  description: string;
  allowedTools: string[];
  compatibility: string;
  license?: string;
  hasReferences: boolean;
  hasScripts: boolean;
}

interface ScientificSkillsSuiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'uk' | 'en' | 'pl';
  onSelectSkill?: (skillName: string) => void;
}

export const ScientificSkillsSuiteModal: React.FC<ScientificSkillsSuiteModalProps> = ({
  isOpen,
  onClose,
  language = 'uk',
  onSelectSkill
}) => {
  const isUk = language === 'uk';
  const isPl = language === 'pl';

  const [skills, setSkills] = useState<ScientificSkillItem[]>([]);
  const [categories, setCategories] = useState<Array<{ categoryUk: string; categoryEn: string; count: number }>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedSkillDetail, setSelectedSkillDetail] = useState<any | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [mcpStats, setMcpStats] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [skillsRes, catsRes, mcpRes] = await Promise.all([
          fetch('/api/scientific-skills/list'),
          fetch('/api/scientific-skills/categories'),
          fetch('/api/mcp/context-mode/stats')
        ]);

        if (skillsRes.ok) {
          const data = await skillsRes.json();
          setSkills(data.skills || []);
        }

        if (catsRes.ok) {
          const catData = await catsRes.json();
          setCategories(catData.categories || []);
        }

        if (mcpRes.ok) {
          const mcpData = await mcpRes.json();
          const text = mcpData?.result?.content?.[0]?.text || 'MCP Context-Mode: Активний 24/7';
          setMcpStats(text);
        }
      } catch (err) {
        console.error('Failed to load scientific skills:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen]);

  const loadSkillDetail = async (skillName: string) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/scientific-skills/detail/${encodeURIComponent(skillName)}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedSkillDetail(data.skill);
      }
    } catch (err) {
      console.error('Failed to fetch detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const filteredSkills = skills.filter(skill => {
    const matchesCat = selectedCategory === 'all' || skill.categoryUk === selectedCategory;
    if (!matchesCat) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      skill.name.toLowerCase().includes(q) ||
      skill.description.toLowerCase().includes(q) ||
      skill.categoryUk.toLowerCase().includes(q) ||
      skill.categoryEn.toLowerCase().includes(q)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/80 backdrop-blur-md overflow-hidden font-mono animate-fade-in">
      <div className="relative w-full max-w-6xl max-h-[92vh] bg-slate-900 border border-purple-500/50 rounded-2xl shadow-2xl flex flex-col text-slate-100 overflow-hidden">
        
        {/* Header */}
        <div className="p-5 sm:p-6 border-b border-slate-800 bg-gradient-to-r from-slate-950 via-purple-950/70 to-slate-900 flex items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-purple-500/30 text-purple-300 border border-purple-400/40 rounded">
                🔬 177 SCIENTIFIC AGENT SKILLS & MCP
              </span>
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded flex items-center gap-1">
                <Activity className="w-3 h-3 text-emerald-400 animate-pulse" />
                <span>Context-Mode MCP: Online</span>
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black uppercase tracking-wide text-white flex items-center gap-2">
              <Microscope className="w-6 h-6 text-purple-400" />
              <span>{isPl ? '177 Modułów Naukowych B+R & MCP' : isUk ? '177 Наукових Скілів R&D та Сервер MCP' : '177 Scientific Agent Skills & MCP'}</span>
            </h2>
            <p className="text-xs text-slate-300 font-mono">
              {isUk 
                ? 'Автономні наукові протоколи (K-Dense, arXiv:2609.00065) з інтеграцією до 100+ баз даних та оптимізацією токенів Context-Mode.'
                : 'Procedural scientific knowledge library for AI agents with 100+ databases and context-mode token rescue.'}
            </p>
          </div>

          <button
            onClick={onClose}
            className="p-2 bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* MCP Status Banner */}
        {mcpStats && (
          <div className="px-5 py-2.5 bg-purple-950/40 border-b border-purple-500/20 flex items-center justify-between gap-3 text-xs text-purple-200">
            <div className="flex items-center gap-2 truncate">
              <Terminal className="w-4 h-4 text-purple-400 shrink-0" />
              <span className="font-mono truncate">
                ⚡ <strong>Context-Mode Engine:</strong> Захист контекстного вікна, ізоляція токенів та індексація 24/7 активні.
              </span>
            </div>
            <span className="text-[10px] text-purple-400 uppercase tracking-widest shrink-0 font-bold">Token Rescue Active</span>
          </div>
        )}

        {/* Search & Categories Bar */}
        <div className="p-4 sm:p-5 border-b border-slate-800 bg-slate-950/60 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-3 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isUk ? 'Пошук серед 177 наукових скілів (напр. biopython, rdkit, timesfm, crispr, dicom, astropy)...' : 'Search 177 scientific skills...'}
              className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-400"
            />
          </div>

          {/* Category Filter Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs scrollbar-thin">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1 rounded-lg text-[11px] font-bold uppercase transition-all shrink-0 cursor-pointer ${
                selectedCategory === 'all'
                  ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {isUk ? `Всі скіли (${skills.length})` : `All (${skills.length})`}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.categoryUk}
                onClick={() => setSelectedCategory(cat.categoryUk)}
                className={`px-3 py-1 rounded-lg text-[11px] font-bold uppercase transition-all shrink-0 cursor-pointer ${
                  selectedCategory === cat.categoryUk
                    ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                    : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                }`}
              >
                {isUk ? `${cat.categoryUk} (${cat.count})` : `${cat.categoryEn} (${cat.count})`}
              </button>
            ))}
          </div>
        </div>

        {/* Body Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
              <RefreshCw className="w-8 h-8 animate-spin text-purple-400" />
              <p className="text-xs uppercase font-bold">Індексування 177 наукових модулів...</p>
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="text-center py-16 text-slate-400 space-y-2">
              <ShieldAlert className="w-8 h-8 text-amber-400 mx-auto" />
              <p className="text-xs font-bold uppercase">Скілів за вашим запитом не знайдено</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredSkills.map((skill) => (
                <div
                  key={skill.name}
                  className="bg-slate-950 border border-slate-800 hover:border-purple-500/60 rounded-xl p-4 flex flex-col justify-between transition-all group hover:shadow-xl hover:shadow-purple-500/10"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
                        {isUk ? skill.categoryUk.split(',')[0] : skill.categoryEn.split(',')[0]}
                      </span>
                      {skill.hasScripts && (
                        <span className="px-1.5 py-0.5 text-[9px] font-mono bg-blue-500/20 text-blue-300 rounded">
                          py-script
                        </span>
                      )}
                    </div>

                    <h3 className="text-sm font-black text-white group-hover:text-purple-300 transition-colors uppercase tracking-tight flex items-center gap-1.5">
                      <Microscope className="w-4 h-4 text-purple-400 shrink-0" />
                      <span>{skill.name}</span>
                    </h3>

                    <p className="text-xs text-slate-300 line-clamp-3 leading-relaxed">
                      {skill.description}
                    </p>

                    {skill.compatibility && (
                      <div className="text-[10px] text-slate-400 bg-slate-900/80 p-1.5 rounded border border-slate-800/80 line-clamp-2">
                        <strong>Вимоги:</strong> {skill.compatibility}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between gap-2">
                    <button
                      onClick={() => loadSkillDetail(skill.name)}
                      className="text-[11px] font-bold text-slate-400 hover:text-white flex items-center gap-1 cursor-pointer"
                    >
                      <BookOpen className="w-3.5 h-3.5" />
                      <span>SKILL.md</span>
                    </button>

                    <button
                      onClick={() => {
                        if (onSelectSkill) onSelectSkill(skill.name);
                        onClose();
                      }}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold uppercase transition-all flex items-center gap-1 cursor-pointer shadow-md shadow-purple-600/30"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Задіяти</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Modal Overlay */}
        {selectedSkillDetail && (
          <div className="absolute inset-0 z-20 bg-slate-950/95 p-6 overflow-y-auto flex flex-col font-mono animate-fade-in">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <div className="space-y-1">
                <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-purple-500/20 text-purple-300 rounded">
                  {selectedSkillDetail.categoryUk}
                </span>
                <h3 className="text-xl font-black text-white uppercase">{selectedSkillDetail.name}</h3>
              </div>
              <button
                onClick={() => setSelectedSkillDetail(null)}
                className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="py-4 space-y-4 text-xs text-slate-300">
              <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 space-y-2">
                <p><strong>Опис:</strong> {selectedSkillDetail.description}</p>
                <p><strong>Сумісність:</strong> {selectedSkillDetail.compatibility || 'Python 3.10+, NumPy'}</p>
                <p><strong>Дозволені інструменти:</strong> {selectedSkillDetail.allowedTools?.join(', ') || 'Read, Write, Edit, Bash'}</p>
              </div>

              <div className="space-y-2">
                <h4 className="text-sm font-bold uppercase text-purple-300">Процедурні правила модуля (SKILL.md):</h4>
                <pre className="p-4 bg-slate-900 border border-slate-800 rounded-xl text-slate-200 overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed max-h-[50vh]">
                  {selectedSkillDetail.instructions}
                </pre>
              </div>
            </div>

            <div className="mt-auto pt-4 border-t border-slate-800 flex justify-end gap-3">
              <button
                onClick={() => setSelectedSkillDetail(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-bold cursor-pointer"
              >
                Закрити
              </button>
              <button
                onClick={() => {
                  if (onSelectSkill) onSelectSkill(selectedSkillDetail.name);
                  setSelectedSkillDetail(null);
                  onClose();
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-xs font-bold uppercase cursor-pointer"
              >
                Задіяти у дослідженні
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 bg-slate-950 flex items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            <span>177 скілів готові до виклику у дослідженнях Deep Research Hub</span>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-bold uppercase transition-colors cursor-pointer"
          >
            Закрити
          </button>
        </div>

      </div>
    </div>
  );
};

export default ScientificSkillsSuiteModal;
